'use client';

import Image from 'next/image';
import { ctaClick } from '@/lib/analytics';
import { getThemeButtonClass } from '../lib/theme';
import { ITheme } from '../types/theme';
import Link from 'next/link';
import Icon from './Icon';

export interface CtaButtonsProps {
  theme?: ITheme;
  includeDemo?: boolean;
  language: 'en' | 'zh';
  /** Page section reported with cta_click (hero, why_choose_us, …). */
  location?: string;
  /** When set, a secondary button opens the demo app in a new window. */
  fullPageDemoUrl?: string;
}

export default function CtaButtons({
  theme = null,
  includeDemo = false,
  language,
  location = 'hero',
  fullPageDemoUrl,
}: CtaButtonsProps) {
  // Standard CTA for all pages
  const primaryCtaText =
    language === 'en' ? 'Get a Free Account' : '免費開始使用';
  // Map language to app language code: en -> en, zh -> cn
  const appLanguage = language === 'en' ? 'en' : 'zh';
  const primaryCtaLink = `https://app.omceanbooking.com/${appLanguage}/register`;

  // Get button classes based on theme
  const buttonClasses = getThemeButtonClass(theme);

  return (
    <div className="flex flex-col gap-6 pt-4 items-center sm:items-start">
      <div className="flex flex-row flex-wrap gap-4 items-center">
        {/* Primary CTA */}
        <Link
          href={primaryCtaLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            ctaClick('get_free_account', location, language, primaryCtaLink)
          }
          className={`inline-flex items-center justify-center px-6 sm:px-8 py-3 text-sm font-semibold rounded-full text-white ${buttonClasses} shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 w-fit min-w-[160px] sm:min-w-[180px]`}
        >
          <Icon icon="user" className="w-5 h-5 mr-2" />
          {primaryCtaText}
        </Link>

        {/* Secondary CTA - Full page demo */}
        {fullPageDemoUrl && (
          <a
            href={fullPageDemoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              ctaClick('full_page_demo', location, language, fullPageDemoUrl)
            }
            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 text-sm font-semibold rounded-full border-2 border-gray-300 dark:border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 w-fit min-w-[160px] sm:min-w-[180px] shadow-md"
          >
            <Icon icon="arrow-top-right-on-square" className="w-5 h-5 mr-2" />
            {language === 'en' ? 'Full Page Demo' : '全螢幕體驗'}
          </a>
        )}

        {/* Secondary CTA - View Demo */}
        {includeDemo && (
          <Link
            href={`/${language}/demo/`}
            onClick={() =>
              ctaClick('view_demo', location, language, `/${language}/demo/`)
            }
            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 text-sm font-semibold rounded-full border-2 border-gray-300 dark:border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 w-fit min-w-[160px] sm:min-w-[180px] shadow-md"
          >
            <Icon icon="play" className="w-5 h-5 mr-2" />
            {language === 'en' ? 'View Demo' : '查看演示'}
          </Link>
        )}
      </div>

      {/* No credit card needed with arrow */}
      <div className="relative">
        <p
          className="text-cyan-500 font-semibold ml-6"
          style={{
            fontFamily:
              language === 'en'
                ? 'Caveat, cursive'
                : "'Zhi Mang Xing', cursive",
          }}
        >
          {language === 'en' ? 'No credit card needed' : '不需信用卡'}
        </p>
        {/* Curved arrow pointing from text to button */}
        <div className="absolute -top-10 -left-5 pointer-events-none">
          <Image
            src="/assets/icons/custom/custom-arrow.svg"
            alt="Arrow"
            width={32}
            height={63}
            className="dark:invert"
            style={{ transform: 'rotate(178deg)' }}
          />
        </div>
      </div>
    </div>
  );
}
